import React, { useEffect, useRef } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import { DraftManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import DraftManualCard from './DraftManualCard';
import DraftManualCardSkeleton from './DraftManualCardSkeleton';

export type DraftManualsGridViewProps = {
  manuals: DraftManualItem[];
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  getVerticalName: (verticalId: string) => string | undefined;
  manualHasPermissions: (manualId: string, permissions: string[]) => boolean;
  buildActionMenuItems: (manual: DraftManualItem) => ActionMenuItemProps[];
  onPublish: (manual: DraftManualItem) => void;
  onExport: (manual: DraftManualItem) => void;
  onManualClick?: (manual: DraftManualItem) => void;
};

const DraftManualsGridView: React.FC<DraftManualsGridViewProps> = ({
  manuals,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  getVerticalName,
  manualHasPermissions,
  buildActionMenuItems,
  onPublish,
  onExport,
  onManualClick,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
        overflowY: 'auto',
        minWidth: 0,
        alignContent: 'start',
      }}
    >
      {isLoading
        ? Array.from({ length: 4 }).map((_, i) => <DraftManualCardSkeleton key={i} />)
        : null}
      {!isLoading && manuals.length === 0 && (
        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <Stack spacing={1} alignItems='center'>
            <Typography variant='subtitleLg' color='text.secondary'>
              {t('draftManuals.list.emptyState')}
            </Typography>
          </Stack>
        </Box>
      )}
      {!isLoading && manuals.map((manual) => (
        <DraftManualCard
          key={manual.id}
          manual={manual}
          verticalName={getVerticalName(manual.verticalId) ?? ''}
          actionMenuItems={buildActionMenuItems(manual)}
          onPublish={onPublish}
          onExport={onExport}
          canPublish={manualHasPermissions(manual.id, ['issuePolicy'])}
          canExport={true}
          onManualClick={
            manualHasPermissions(manual.id, ['lockDraftManual']) ? onManualClick : undefined
          }
        />
      ))}
      <div ref={sentinelRef} style={{ gridColumn: '1 / -1', height: 1 }} />
    </Box>
  );
};

export default DraftManualsGridView;
